import * as React from "react"
import {useSignal} from "@preact/signals-react";
import {Box, Typography, useTheme} from "@mui/material";
import {CardSeeker, IconSeeker, PopoverSeeker} from "@ui-seeker/core"
import {capitalize, formatDate, formatNumber, formatPercentage} from "@ui-seeker/utils";

import {GROUPED_BY} from "@constants";
import {SETUP_COMPARISON} from "../../../@constants";
import {useRepricingFilters} from "../../@hooks";
import RepricingAlertCardAction from "./Action";
import {RepricingAlertCardRowBasic, RepricingAlertCardRowShared} from "./Rows";
import RepricingAlertCardTitle from "./Title";
import {cardStyles} from "./styles";


const _formatDate = date => formatDate(date, { isString: true })
const _formatPercentage = value => formatPercentage(value, { decimals: 1 })
const comparisonTranslator = value => ({
	[SETUP_COMPARISON.LOWER]: 'Lower',
	[SETUP_COMPARISON.HIGHER]: 'Higher',
	[SETUP_COMPARISON.HIGHER_OR_LOWER]: 'Higher or lower',
})[value]
const normalizeScopeKey = key => ({
	[GROUPED_BY.BRAND]: 'Brand',
	[GROUPED_BY.RETAILER]: 'Retailer',
	[GROUPED_BY.SKU]: 'SKU',
	[GROUPED_BY.STOCK]: 'Stock'
})[key] || capitalize(key)

export default function RepricingAlertCard({ idx, ...props }) {
	const { ruleID, name, dataScope, action, actionType, actionValue, priceRule, priceRuleValue, scheduleDateStart, scheduleDateEnd } = props
	const emails = props.email || []
	const frequency = props.frequency || []

	const { palette } = useTheme()
	const { isFiltered, handleClicked } = useRepricingFilters(ruleID)
	const isOpenPopover = useSignal(null)

	const isPercentage = actionType === 'percentage'
	const haveMails = emails?.length
	const hourFormatted = (props.hour || []).map(a => formatDate(a, {
		isString: true,
		format: 'HH:mm',
		receivedFormat: 'HH'
	})).join(', ')

	const showScheduleBlock = hourFormatted.length || frequency.length || scheduleDateStart || scheduleDateEnd

	return <CardSeeker
		className={isFiltered ? 'is-filtered' : ''}
		title={<RepricingAlertCardTitle alertID={ruleID} name={name} haveMails={haveMails} data={props} />}
		actions={<RepricingAlertCardAction enabled={props.enabled} alertID={ruleID} idx={idx} />}
		sx={cardStyles(palette)}
	>
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }} onClick={handleClicked}>
			<Typography variant="caption">
				<IconSeeker icon="sliders" sx={{ mr: .2 }} /> Scope
			</Typography>

			{ dataScope.slice(0, 2).map(({ key, label, operation }) => <RepricingAlertCardRowBasic
				key={key}
				label={normalizeScopeKey(key)}
				value={`${capitalize(operation)} ${label.join(", ")}`}
			/>) }

			{ dataScope.length > 2 && <Typography
				variant="caption"
				sx={{ color: 'primary.main', cursor: 'pointer' }}
				onClick={e => isOpenPopover.value = e.currentTarget}
			>
				View More
			</Typography> }

			<PopoverSeeker
				anchorEl={isOpenPopover.value}
				onClose={() => isOpenPopover.value = null}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				arrowProps={{ sx: { display: 'none' } }}
			>
				{ dataScope.slice(2).map(({ key, label, operation }) => <RepricingAlertCardRowBasic
					key={key}
					label={normalizeScopeKey(key)}
					value={`${capitalize(operation)} ${label.join(", ")}`}
				/>) }
			</PopoverSeeker>

			<Typography variant="caption">
				<IconSeeker icon="gear" sx={{ mr: .2 }} /> Trigger
			</Typography>

			<RepricingAlertCardRowBasic
				label="Rule"
				value={`${comparisonTranslator(priceRule)} (${_formatPercentage(priceRuleValue)})`}
			/>

			<Typography variant="caption">
				<IconSeeker icon="square-caret-right" sx={{ mr: .2 }} /> Action
			</Typography>

			{ !!actionValue && <RepricingAlertCardRowBasic
				label={capitalize(action)}
				value={isPercentage ? _formatPercentage(actionValue) : formatNumber(actionValue)}
			/> }

			{ !!haveMails && <RepricingAlertCardRowShared emails={emails} /> }

			{ !!showScheduleBlock && <>
				<Typography variant="caption">
					<IconSeeker icon="calendar-clock" sx={{ mr: .2 }} /> Schedule
				</Typography>
				{ !!hourFormatted.length && <RepricingAlertCardRowBasic label="Execution hours" value={hourFormatted} /> }
				{ !!frequency.length && <RepricingAlertCardRowBasic label="Filter Days" value={frequency} /> }
				{ !!scheduleDateStart && !!scheduleDateEnd && <RepricingAlertCardRowBasic
					label="Execution dates"
					value={`${_formatDate(scheduleDateStart)} - ${_formatDate(scheduleDateEnd)}`}
				/> }
			</> }
		</Box>
	</CardSeeker>
}